import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ConnectionsCard } from '@/components/dashboard/connections-card';
import { Activity, Calendar, Users, ArrowRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format, subDays } from 'date-fns';

export function StandardDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    profileViews: 0,
    activityScore: 0,
    upcomingInterviews: 0,
    viewTrend: [] as { date: string; views: number }[],
    recentViewers: [] as { id: string; full_name: string; avatar_url: string | null }[]
  });

  useEffect(() => {
    if (user) {
      fetchMetrics();
    }
  }, [user]);

  const fetchMetrics = async () => {
    if (!user) return;

    try {
      // Get the date range for the past 7 days
      const endDate = new Date();
      const startDate = subDays(endDate, 6);

      // Fetch profile views with daily trend
      const { data: viewsData } = await supabase
        .from('profile_views')
        .select(`
          created_at,
          viewer:profiles!viewer_id(
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('profile_id', user.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });

      // Process views data into daily trend
      const dailyViews = new Map<string, number>();
      const viewers = new Set<string>();
      const recentViewers: typeof metrics.recentViewers = [];

      // Initialize all days with 0 views
      for (let i = 0; i <= 6; i++) {
        const date = format(subDays(endDate, i), 'yyyy-MM-dd');
        dailyViews.set(date, 0);
      }

      // Count views per day and collect unique viewers
      viewsData?.forEach(view => {
        const date = format(new Date(view.created_at), 'yyyy-MM-dd');
        dailyViews.set(date, (dailyViews.get(date) || 0) + 1);

        if (view.viewer && !viewers.has(view.viewer.id)) {
          viewers.add(view.viewer.id);
          if (recentViewers.length < 5) {
            recentViewers.push({
              id: view.viewer.id,
              full_name: view.viewer.full_name,
              avatar_url: view.viewer.avatar_url
            });
          }
        }
      });

      // Convert daily views map to array
      const viewTrend = Array.from(dailyViews.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, views]) => ({ date, views }));

      // Fetch upcoming interviews
      const { data: interviewsData } = await supabase
        .from('interviews')
        .select('id')
        .eq('status', 'scheduled')
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true });

      // Calculate activity score based on profile completeness and engagement
      const { data: profileData } = await supabase
        .from('profiles')
        .select(`
          *,
          experiences (id),
          education (id),
          projects (id)
        `)
        .eq('id', user.id)
        .single();

      let activityScore = 0;
      if (profileData) {
        if (profileData.full_name) activityScore += 10;
        if (profileData.title) activityScore += 10;
        if (profileData.summary) activityScore += 10;
        if (profileData.location) activityScore += 5;
        if (profileData.experiences?.length > 0) activityScore += 25;
        if (profileData.education?.length > 0) activityScore += 20;
        if (profileData.projects?.length > 0) activityScore += 20;
      }

      setMetrics({
        profileViews: viewers.size,
        activityScore: Math.min(activityScore, 100),
        upcomingInterviews: interviewsData?.length || 0,
        viewTrend,
        recentViewers
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Track your professional growth and opportunities</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Profile Views</p>
              <h3 className="text-3xl font-bold mt-2">{metrics.profileViews}</h3>
            </div>

            {/* Trend Graph */}
            <div className="mt-6 h-[60px] w-full relative">
              <div className="flex items-end justify-between h-full gap-1">
                {metrics.viewTrend.map((day, i) => (
                  <div 
                    key={day.date}
                    className="relative flex-1"
                    title={`${format(new Date(day.date), 'MMM d')}: ${day.views} views`}
                  >
                    <div 
                      className="absolute bottom-0 w-full bg-gradient-to-t from-primary/20 to-transparent rounded-sm transition-all duration-300"
                      style={{ 
                        height: '100%',
                        opacity: day.views > 0 ? '1' : '0.1'
                      }}
                    />
                  </div>
                ))}
              </div>
              
              {metrics.profileViews === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                  No one has viewed your profile over the past 7 days
                </div>
              )}
            </div>

            {/* Recent Viewers */}
            {metrics.recentViewers.length > 0 && (
              <div className="mt-6 flex -space-x-2 overflow-hidden">
                {metrics.recentViewers.map((viewer) => (
                  <Avatar 
                    key={viewer.id} 
                    className="inline-block border-2 border-background"
                  >
                    <AvatarImage src={viewer.avatar_url || undefined} alt={viewer.full_name} />
                    <AvatarFallback>{viewer.full_name[0]}</AvatarFallback>
                  </Avatar>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">NewLoop Activity</p>
                <h3 className="text-2xl font-bold mt-2">{metrics.activityScore}%</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Activity className="h-6 w-6 text-primary" />
              </div>
            </div>
            <Progress value={metrics.activityScore} className="mt-4 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Upcoming Interviews</p>
                <h3 className="text-2xl font-bold mt-2">{metrics.upcomingInterviews}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Career Journey Card */}
      <Card className="shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Career Journey</CardTitle>
          </div>
          <CardDescription>Track your progress toward your career goals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button asChild>
              <Link to="/planner">
                Start Career Planning
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Professional Network */}
      <ConnectionsCard />
    </div>
  );
}